import {
  ref as dbRef, push, set,
} from 'firebase/database';
import { signInAnonymously } from 'firebase/auth';
import { db, auth } from '../firebase';
import { gatherAppState, installErrorCapture } from './bugContext';

// Start remembering errors as soon as anything imports this, so a report can
// say what threw before the player hit the button.
installErrorCapture();

// Everything around the game state - route, room, seat, connection, recent
// errors, build (utils/bugContext.js). A JSON string for the same reason
// gameState is one, and because RTDB refuses undefined outright. Any failure
// here yields null: a broken snapshot must never stop a report from sending.
function appStateFor(store, route) {
  try {
    return JSON.stringify(gatherAppState({ store, route }));
  } catch {
    return null;
  }
}

// Ported from the thunderstone repo's own utils/bugReports.js - same
// pipeline (capture the transcript + full game state, write to Firebase
// RTDB, read back via scripts/fetch-bug-reports.mjs). See CLAUDE.md - no
// Firebase project exists for Space Base yet, so this throws a clear
// "not configured" error until Matt sets one up; local hotseat play
// doesn't depend on it.

// Matt reported this directly: "We should allow bug reports even when
// we're offline in this app and then when you get back online, the next
// time they should all get reported." The Firebase RTDB client CAN queue
// writes made while offline, but only in memory for the current page
// load - a reload (or just losing the tab) while still offline loses
// anything unsent. Persisting the report to localStorage BEFORE
// attempting the real write, and only clearing it out once that write
// actually succeeds, survives both: a page reload replays whatever's
// still sitting in the queue (flushQueuedBugReports, called once at
// startup in main.js) instead of just the in-memory retry the SDK
// already does on its own.
const QUEUE_KEY = 'spaceBaseQueuedBugReports';

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    // A corrupted/unparseable queue shouldn't ever block a NEW report
    // from being submitted - treat it as empty rather than throwing.
    return [];
  }
}

function writeQueue(queue) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
}

async function writeReport(report) {
  // Namespaced, not "bugReports" - this project's RTDB is shared with the
  // thunderstone repo (see CLAUDE.md's "Firebase" section) and that key
  // is already thunderstone's own bug-report inbox.
  await set(push(dbRef(db, 'spaceBaseBugReports')), report);
}

export async function submitBugReport(store, transcript, { route } = {}) {
  if (!db || !auth) throw new Error("Firebase is not configured (see .env.example) - can't send bug reports locally.");
  const text = (transcript || '').trim();
  if (!text) throw new Error('Describe what happened before sending.');

  const { user, gameState, remoteGameId } = store.state;
  const report = {
    // A real timestamp, not serverTimestamp()'s own sentinel object -
    // this needs to survive a JSON.stringify/parse round trip through
    // localStorage if it ends up queued below, which a serverTimestamp()
    // placeholder can't.
    createdAt: Date.now(),
    transcript: text,
    reporterUid: auth.currentUser?.uid || null,
    reporterDisplayName: user?.displayName || null,
    url: window.location.href,
    userAgent: navigator.userAgent,
    // Matt reported this directly: "when we do bug logs like this, they
    // should report device type and screen size if possible." userAgent
    // alone names the device but not the actual viewport - the more
    // actionable piece for layout reports (his own earlier iPad
    // padding/whitespace complaints would've been much faster to
    // diagnose with this attached).
    screenSize: `${window.innerWidth}x${window.innerHeight}`,
    devicePixelRatio: window.devicePixelRatio || 1,
    remoteGameId: remoteGameId || null,
    // Stringified, not a raw nested object - RTDB drops empty-object keys
    // silently on write (see the thunderstone repo's own comment on this
    // exact bug for the story behind it).
    gameState: gameState ? JSON.stringify(gameState) : null,
    appState: appStateFor(store, route),
  };

  try {
    if (!auth.currentUser) await signInAnonymously(auth);
    report.reporterUid = auth.currentUser.uid;
    await writeReport(report);
    return { queued: false };
  } catch {
    // Offline (or any other transient failure, e.g. signInAnonymously
    // itself needing a network round trip) - keep the report instead of
    // losing it. Not re-thrown: from the reporter's own point of view
    // this still worked, it just hasn't reached the server yet.
    writeQueue([...readQueue(), report]);
    return { queued: true };
  }
}

// Called once at app startup (main.js) and again on the browser's own
// 'online' event, so a queued report goes out the moment connectivity
// actually returns rather than waiting for the next bug report to be
// filed. Best-effort per report - one that still fails (still offline,
// or a genuine write error) stays queued for the next attempt instead of
// being dropped.
export async function flushQueuedBugReports() {
  if (!db || !auth) return;
  const queue = readQueue();
  if (!queue.length) return;

  if (!auth.currentUser) {
    try {
      await signInAnonymously(auth);
    } catch {
      return;
    }
  }

  const remaining = [];
  for (const report of queue) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await writeReport({ ...report, reporterUid: report.reporterUid || auth.currentUser.uid });
    } catch {
      remaining.push(report);
    }
  }
  writeQueue(remaining);
}
